import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import GridFilterPanel from '../../../commons/filters/GridFilterPanel.jsx';
import * as Utils from '../../../commons/utils/Utils.js';
import { ANIOS } from '../../../commons/constants/anios.js';
import {
	MOVILES_INMOVILIZADOS_FILTROS_STORAGE_KEY,
	buildMovilesInmovilizadosFilterFields,
	getMovilesInmovilizadosFilterDefaults,
} from './movilesInmovilizadosFilterFields.js';

/**
 * Filtros del reporte de Móviles Inmovilizados.
 *
 * VEC-3696: este componente pasó de tener el panel escrito a mano (601 líneas, 13 `<Select>`
 * de MUI repetidos, su propio estado y su propia persistencia con switch) a ser sólo la capa
 * que ALIMENTA a <GridFilterPanel />: carga las opciones y las normaliza a `{value, label}`.
 * El estado, la persistencia y la serialización son del panel compartido; ver
 * docs/design-system/grid-filter-panel.md.
 *
 * Los nombres de campo y los tipos de control se mantuvieron, así el query que arma la grilla
 * no cambió. Pasar los desplegables a múltiples es la fase 2.
 */

const OPTIONS_INIT = {
	tipoDeTicket: [],
	movilEstado: [],
	base: [],
	region: [],
	subregion: [],
	servicio: [],
	gerenciador: [],
	abono: [],
	funcion: [],
	marca: [],
	modelo: [],
	anio: [],
	tipoVehiculo: [],
};

const MovilesInmovilizadosGridFiltros = (props) => {
	const intl = useIntl();
	const [options, setOptions] = useState(OPTIONS_INIT);
	const [optionsLoading, setOptionsLoading] = useState(true);

	const fields = buildMovilesInmovilizadosFilterFields();

	// Los tres combos que no salen de un endpoint: estaban hardcodeados en el panel viejo.
	const tiposDeTicket = [
		{ value: 'CORRECTIVO', label: intl.formatMessage({ id: 'movilesInmovilizadosGridFiltros.select_correctivo', defaultMessage: 'CORRECTIVO' }) },
		{ value: 'PREVENTIVO', label: intl.formatMessage({ id: 'movilesInmovilizadosGridFiltros.select_preventivo', defaultMessage: 'PREVENTIVO' }) },
		{ value: 'VENCIMIENTO', label: intl.formatMessage({ id: 'movilesInmovilizadosGridFiltros.select_vencimiento', defaultMessage: 'VENCIMIENTO' }) },
	];

	const estadosDeVehiculo = [
		{ value: 'OPERATIVO', label: 'OPERATIVO' },
		{ value: 'BACKUP', label: 'BACKUP' },
	];

	// El panel viejo mostraba "Abono SI" / "Abono NO" en el renderValue, pero "SI"/"NO" en las
	// opciones del desplegable. Se conserva lo que se leía AL ELEGIR, que es la lista.
	const abonos = [
		{ value: 'SI', label: intl.formatMessage({ id: 'movilesInmovilizadosGridFiltros.select_abono_si', defaultMessage: 'SI' }) },
		{ value: 'NO', label: intl.formatMessage({ id: 'movilesInmovilizadosGridFiltros.select_abono_no', defaultMessage: 'NO' }) },
	];

	useEffect(() => {
		setOptionsLoading(true);

		Promise.all([
			Utils.getData('/bases/select-filtered-by-user/base/'),
			Utils.getData('/servicios/select'),
			Utils.getData('/regiones/select'),
			Utils.getData('/subregiones/select-all'),
			Utils.getData('/gerenciadores/select-filtered-by-user'),
			Utils.getData('/marcas/select'),
			Utils.getData('/modelo-tipos/select'),
			Utils.getData('/funciones/select'),
		])
			.then(([bases, servicios, regiones, subregiones, gerenciadores, marcas, tiposVehiculo, funciones]) => {
				setOptions((prev) =>
					Object.assign({}, prev, {
						base: bases,
						servicio: servicios,
						region: regiones,
						subregion: subregiones,
						gerenciador: gerenciadores,
						marca: marcas,
						tipoVehiculo: tiposVehiculo,
						funcion: funciones,
						anio: ANIOS,
						tipoDeTicket: tiposDeTicket,
						movilEstado: estadosDeVehiculo,
						abono: abonos,
					})
				);
				setOptionsLoading(false);
			})
			.catch(() => setOptionsLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * *** `modelo` DEPENDE DE `marca`: es el único caso de desplegable dependiente del patrón. ***
	 *
	 * Sus opciones salen de `/modelos/marca/{id}/select`, así que hay que recargarlas apenas
	 * cambia la marca —no al apretar "Buscar"—. Para eso existe `onFieldChange`, la prop
	 * aditiva que se le agregó al panel en esta card: avisa la edición en el momento.
	 *
	 * El panel viejo hacía lo mismo con un `useEffect` sobre `props.filters.marca`, que acá no
	 * sirve porque el estado de edición vive adentro del panel.
	 *
	 * Desde la fase 2 `marca` es MÚLTIPLE, así que los modelos son la unión de los de cada
	 * marca elegida: un pedido por marca, en paralelo. El endpoint es por marca y no acepta una
	 * lista; con dos o tres marcas elegidas son dos o tres pedidos, que es lo que hay.
	 */
	const pedidoDeModelos = useRef('');
	const modelosDelEstadoInicial = useRef(false);

	/** Carga los modelos de las marcas indicadas, descartando lo que llegue tarde. */
	const cargarModelos = (marcas) => {
		// *** LAS RESPUESTAS PUEDEN LLEGAR DESORDENADAS. *** Eligiendo marcas rápido, el
		// resultado de un pedido viejo puede pisar al nuevo y dejar una lista que no
		// corresponde a lo elegido. Se guarda cuál es el pedido vigente y se descarta lo que
		// llegue tarde.
		const vigente = marcas.slice().sort().join(',');
		pedidoDeModelos.current = vigente;

		if (marcas.length === 0) {
			setOptions((prev) => Object.assign({}, prev, { modelo: [] }));
			return;
		}

		Promise.all(marcas.map((id) => Utils.getData(`/modelos/marca/${id}/select`)))
			.then((respuestas) => {
				if (pedidoDeModelos.current !== vigente) return;

				const porValue = {};
				respuestas.forEach((modelos) => {
					(modelos || []).forEach((m) => {
						porValue[m.value] = m;
					});
				});
				setOptions((prev) => Object.assign({}, prev, { modelo: Object.values(porValue) }));
			})
			.catch(() => {
				if (pedidoDeModelos.current !== vigente) return;
				setOptions((prev) => Object.assign({}, prev, { modelo: [] }));
			});
	};

	const handleFieldChange = (name, valor) => {
		if (name !== 'marca') return;

		cargarModelos(
			(Array.isArray(valor) ? valor : [valor])
				.filter((o) => o && o.value !== undefined && o.value !== null)
				.map((o) => o.value)
		);
	};

	/**
	 * *** `applyOnMount` NO DISPARA `onFieldChange`, SÓLO `onApply`. ***
	 *
	 * Al reingresar con una marca persistida, los chips se ven pero el desplegable de modelos
	 * queda VACÍO: nadie pidió sus opciones, porque el usuario no editó nada. El resultado es
	 * que no puede elegir un modelo hasta volver a tocar la marca.
	 *
	 * Se resuelve leyendo el query que el panel entrega al montar —ahí viene `marca` ya
	 * serializada, "3,5"— y cargando los modelos una sola vez. No hace falta que el wrapper
	 * sepa nada de la persistencia: el panel ya le está diciendo con qué arrancó.
	 */
	const handleApply = (query) => {
		if (!modelosDelEstadoInicial.current) {
			modelosDelEstadoInicial.current = true;
			const marcas = String(query.marca || '').split(',').filter((v) => v !== '');
			if (marcas.length > 0) cargarModelos(marcas);
		}

		if (props.onApply) props.onApply(query);
	};

	return (
		<GridFilterPanel
			open={props.dropdownActive}
			fields={fields}
			options={options}
			defaults={getMovilesInmovilizadosFilterDefaults()}
			storageKey={MOVILES_INMOVILIZADOS_FILTROS_STORAGE_KEY}
			loading={optionsLoading}
			resetSignal={props.filterResetInputs}
			onResetSignalHandled={() => props.setFilterResetInputs(false)}
			onFieldChange={handleFieldChange}
			onApply={handleApply}
			applyOnMount
		/>
	);
};

export default MovilesInmovilizadosGridFiltros;
